// @ts-nocheck

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Cross } from '../../assets/svg/common';
import { SportsArrow } from '../../assets/svg/sports-menu';
import { Target } from '../../client';
import { getPortalRootDOMElement } from '../../DOMElements';
import theme from '../../styled-components/dark';
import { ZIndices } from '../../styled-components/dark/common';

const HEIGHT = 2;

const Wrap = styled.div`
  position: relative;
  z-index: ${({ $isOpen }) => ($isOpen ? ZIndices.overlay + 1 : 'auto')};
  border-radius: 1em;
  color: ${theme.CatalogFilters.color};

  transition: border-radius 150ms ease-in-out;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  ${({ $isOpen }) => {
    if ($isOpen) {
      return css`
        border-bottom-left-radius: 0em;
        border-bottom-right-radius: 0em;
      `;
    }
  }}
`;

const Button = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  height: ${HEIGHT}em;
`;

const Label = styled.p`
  margin: 0;
  width: calc(100% - 6em);
  overflow: hidden;
  position: relative;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 1em;
`;

const CancelButton = styled.div`
  display: inline-block;
  padding: 0.3em;
  position: absolute;
  top: 0;
  left: 0;
  width: 2.5em;
  height: ${HEIGHT}em;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 300ms ease-in-out;
  transform: ${({ $isHide }) => ($isHide ? 'scale(0)' : 'none')};
  background: transparent;
`;

const WrapArrow = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  width: 2.5em;
  height: ${HEIGHT}em;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ArrowUpSvg = styled(SportsArrow)`
  display: inline-block;
  width: 0.7em;
  height: auto;
  transition: transform 300ms ease-in-out;
  transform: ${({ $isSelecting }) => ($isSelecting ? 'translateZ(0) rotate(0deg)' : 'translateZ(0) rotate(180deg)')};
`;

const CloseSvg = styled(Cross)`
  height: 0.5em;
  color: ${theme.CatalogFilters.closeSvgColor};
`;

const List = styled.div`
  max-height: 60vh;
  position: absolute;
  width: 100%;
  top: ${HEIGHT}em;
  z-index: 9999;
  border-radius: 0 0 0.4em 0.4em;
  overflow: hidden;
  transform-origin: top center;
  transition:
    opacity 200ms ease-out,
    transform 200ms ease-in-out;

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          opacity: 1;
          transform: none;
        `
      : css`
          opacity: 0;
          transform: scale(1, 0);
        `}
`;

const BackgroundBehindButton = styled.div`
  position: absolute;
  width: calc(100% - 1.2em);
  box-sizing: border-box;
  left: 0.6em;
  top: ${({ $isSecondary }) => ($isSecondary ? '3em' : '1em')};
  height: calc(100% - 1em);
  padding: 0 0.6em;
  background: #1a1c24;
`;

const ListItem = styled.p`
  font-size: 1em;
  color: #c1c9cf;
  padding: 0.4em 1.5em;
  margin: 0;
  cursor: pointer;
  transition: all 60ms ease-in-out;
  background: #1a1c24;
  border-bottom: solid 1px rgba(255, 255, 255, 0.1);

  &:nth-of-type(2n) {
    background-image: linear-gradient(to right, #2b2e38 0%, rgba(25, 26, 34, 0.4) 37%, rgba(18, 18, 19, 0));
  }
  &:last-of-type {
    border-bottom: none;
  }

  ${Target.__MOBILE__
    ? undefined
    : css`
        &:hover {
          background: #43485a;
        }
      `}
`;

type Filter = {
  id: number;
  label: string;
  effectiveId: number | null;
};

type BetFilterSelectorProps = {
  className?: string;
  options: Array<Filter>;
  selectId?: number;
  onSelect: (id: number | null) => void;
  defaultLabel?: string;
  noCancelButton?: boolean;
  isSecondary?: boolean;
};

const BetFilterSelector = ({
  className,
  options,
  selectId,
  onSelect,
  defaultLabel = '',
  noCancelButton = false,
  isSecondary = false,
}: BetFilterSelectorProps) => {
  const content = useRef(null);
  const [isOpen, setIsOpened] = useState(false);

  const toggle = useCallback(() => {
    setIsOpened((old) => !old);
  }, []);

  const cancel = useCallback(
    (e) => {
      e.stopPropagation();
      setIsOpened(false);
      onSelect(null);
    },
    [onSelect],
  );

  const handleSelect = useCallback(
    (id) => {
      setIsOpened(false);
      if (selectId !== id) {
        onSelect(id);
      }
    },
    [onSelect, selectId],
  );

  const label = useMemo(() => {
    if (selectId) {
      const option = options.filter((o) => o.id === selectId);
      if (option[0]) {
        return option[0].label;
      }
    }
    return defaultLabel;
  }, [defaultLabel, options, selectId]);

  const handleOutsideClick = useCallback(
    (e) => {
      if (!content.current.contains(e.target) && isOpen) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpened(false);
      }
    },
    [isOpen],
  );

  useLayoutEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const disableScroll = useCallback((e) => {
    e.preventDefault();
  }, []);

  useLayoutEffect(() => {
    const portalRootDOMElement = getPortalRootDOMElement();
    if (Target.__MOBILE__ && isOpen && portalRootDOMElement) {
      portalRootDOMElement.addEventListener('touchmove', disableScroll, false);
      portalRootDOMElement.addEventListener('scroll', disableScroll, false);
    }
    return () => {
      if (Target.__MOBILE__ && isOpen && portalRootDOMElement) {
        portalRootDOMElement.removeEventListener('touchmove', disableScroll, false);
        portalRootDOMElement.removeEventListener('scroll', disableScroll, false);
      }
    };
  }, [disableScroll, isOpen]);

  return (
    <>
      {isOpen ? <BackgroundBehindButton $isSecondary={isSecondary} /> : null}
      <Wrap className={className} ref={content} $isOpen={isOpen}>
        <Button className="selector-button" onClick={toggle}>
          {!noCancelButton ? (
            <CancelButton $isHide={selectId === null} onClick={cancel}>
              <CloseSvg />
            </CancelButton>
          ) : null}
          <Label>{label}</Label>
          <WrapArrow>
            <ArrowUpSvg $isSelecting={isOpen} />
          </WrapArrow>
        </Button>
        <List className="selector-list" $isOpen={isOpen}>
          {isOpen
            ? options.map((option) => (
                <ListItem
                  highlighted={option.id === selectId}
                  onClick={() => handleSelect(option.effectiveId || option.id)}
                  key={option.id}
                >
                  {option.label}
                </ListItem>
              ))
            : null}
        </List>
      </Wrap>
    </>
  );
};

export default BetFilterSelector;
